import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { EventDesignPurpose, EventUserStatus, useEventWebsiteGuestLazyQuery, useGetEventCTAButtonsDataLazyQuery, useGetGuestSiteScheduleLazyQuery } from '@graphql/generated';
import { AddToCalendar } from '@shared/components/AddToCalendar';
import { useAuth } from '@shared/components/AuthProvider';
import { Skeleton } from '@shared/components/Skeleton';
import { useFeatureValue } from '@shared/core/featureFlags';
import { STANDARD_TRANSITION } from '@shared/utils/animationTransition';
import { useEventInfo } from '@shared/utils/eventInfo';
import { withWindow } from '@shared/utils/withWindow';
import { Box, BoxProps, ButtonV2, DrawerV2, JoyKitThemeProvider, PopoverV2, useDisclosure } from '@withjoy/joykit';
import { AddToCalendar as AddToCalendarIcon, Car, ChevronRight, Close, DiscountCode, Gift, GuestGuide as GuestGuideIcon, Rsvp } from '@withjoy/joykit/icons';
import { BreakpointAliasKey, pxToRem, styled, useTheme } from '@withjoy/joykit/theme';
import React, { useEffect } from 'react';
import { addressStr } from '@shared/utils/formatting/location.formats';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useTranslation } from '@shared/core';
import { GuestGuideTelemetryProvider, useGuestGuideTelemetry } from './GuestGuideTelemetry';

const ALOHA_BREAKPOINT: BreakpointAliasKey = 'sm4';

/**
 * This component relies on EventInfoProvider
 * @returns element for guest guide
 */
export const GuestGuide = () => {
  const { eventInfo, eventHandle, loading: loadingEventInfo } = useEventInfo();
  const { currentUser } = useAuth();
  const [getCtaData, { data: sessionData, called: calledCTA, loading: loadingCTA }] = useGetEventCTAButtonsDataLazyQuery({
    batchMode: 'fast',
    ssr: false
  });

  const [getEventData, { data: eventData }] = useEventWebsiteGuestLazyQuery({
    batchMode: 'fast',
    ssr: false,
    fetchPolicy: 'cache-only'
  });
  useEffect(() => {
    if (eventHandle) {
      getCtaData({
        variables: { name: eventHandle }
      });
      getEventData({
        variables: {
          eventDesignPurpose: EventDesignPurpose.live,
          eventHandle: eventHandle,
          isAdminDashboard: false
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventHandle]);
  const { getButtonsVisibility } = useCtaButtons();
  const { welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, customPages } = sessionData?.eventByName || {};
  const { eventCTA, hasUnlocked } = sessionData?.eventByName?.activeSession || {};
  const { rsvpIsEnabled, scheduleHasInviteOnlyItems } = eventCTA || {};
  const pages = [welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, ...(customPages ?? [])];
  const rsvpIsPasswordProtected = rsvpPage?.private;
  const { shouldDisplayUnlockButton } = getButtonsVisibility(pages, welcomePage, schedulePage, rsvpIsEnabled, rsvpIsPasswordProtected, scheduleHasInviteOnlyItems, hasUnlocked);

  // sm4 is the breakpoint for desktop on aloha
  const isDesktop = useMediaQuery(theme => theme.mediaQueries.up({ breakpointAlias: ALOHA_BREAKPOINT }));
  const showGuestGuide = useFeatureValue('showGuestGuide');

  const hasEventPast = (eventInfo?.finalizedDate?.milliseconds || Infinity) < new Date().getMilliseconds();
  const isAdminOrOwner = ([EventUserStatus.admin, EventUserStatus.owner] as Maybe<EventUserStatus>[]).includes(currentUser.activeEventUser?.status);
  const accommodationsPageActive = eventData?.eventByName?.pages?.find(p => p.pageSlug === 'accommodations')?.disabled === false;
  const hasRsvpD = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsCompleted;
  // const hidden = !calledCTA || loadingEventInfo || loadingCTA || !eventInfo || !hasRsvpDYes || !isGuest || hasEventPast || accommodationsPageActive;
  const hidden =
    showGuestGuide !== 'true' &&
    [!calledCTA, loadingEventInfo, loadingCTA, !eventInfo, isAdminOrOwner, hasEventPast, shouldDisplayUnlockButton, !hasRsvpD, accommodationsPageActive].some(Boolean);
  // eslint-disable-next-line no-console
  const actions = {
    registry: registryPage?.disabled === false,
    rsvp: rsvpPage?.disabled === false,
    calendar: !!eventInfo?.finalizedDate?.milliseconds,
    cars: false,
    hotels: true
  };
  return hidden ? null : (
    // it's easier to measure experiment if the assignment only happens when the Guide should show
    <GuestGuideTelemetryProvider>
      <GuestGuideFlag>
        <>
          {isDesktop ? (
            <DesktopWrapper trigger={<GuestGuideTrigger />}>
              <GuestGuideContent actions={actions} />
            </DesktopWrapper>
          ) : (
            <MobileWrapper trigger={<GuestGuideTrigger />}>
              <GuestGuideContent actions={actions} />
            </MobileWrapper>
          )}
        </>
      </GuestGuideFlag>
    </GuestGuideTelemetryProvider>
  );
};

const GuestGuideFlag = ({ children }: { children: React.ReactNode }) => {
  const { value, loading } = useFeatureValue('guestguideexperiment');
  const hidden = loading || value !== 'treatment';
  return hidden ? null : <>{children}</>;
};

const GuestGuideTrigger = React.forwardRef<HTMLButtonElement>((props, ref) => {
  const { t } = useTranslation('guestGuide');
  return (
    <Box
      ref={ref}
      as="button"
      position={'fixed'}
      // bottom={[pxToRem(16), pxToRem(44)]}
      bottom={{
        _: pxToRem(16),
        [ALOHA_BREAKPOINT]: pxToRem(44)
      }}
      // right={['50%', pxToRem(44)]}
      right={{
        _: '50%',
        [ALOHA_BREAKPOINT]: pxToRem(44)
      }}
      // transform={[`translate(50%, 0)`, 'translate(0, 0)']}
      transform={{
        _: `translate(50%, 0)`,
        [ALOHA_BREAKPOINT]: 'translate(0, 0)'
      }}
      height={pxToRem(56)}
      backgroundColor={'#000000'}
      color={'white'}
      borderRadius={'full'}
      display={'flex'}
      alignItems={'center'}
      gap={3}
      zIndex={100}
      typographyVariant={'button2'}
      paddingX={6}
      transition={`background-color ${STANDARD_TRANSITION}`}
      _hover={{
        backgroundColor: 'mono13'
      }}
      _active={{
        backgroundColor: 'mono14'
      }}
      // need to spread box props for trigger of Popover
      {...props}
    >
      <GuestGuideIcon />
      {t('buttonText')()}
    </Box>
  );
});

const MobileWrapper = ({ children, trigger }: { children: React.ReactNode; trigger: React.ReactElement }) => {
  const { onClose, isOpen, onOpen } = useDisclosure();
  const handleTrigger = () => {
    onOpen();
  };
  const triggerClone = React.cloneElement(trigger, {
    onClick: handleTrigger
  });
  return (
    <>
      <DrawerV2
        onClose={onClose}
        // disableAutoFocus
        size="auto"
        // overrides={DRAWER_OVERRIDES}
        isOpen={isOpen}
        anchor="bottom"
        useBackdrop
        overrides={{
          // grabbed from admin styles in commonDialog.styles.tsx
          Content: {
            props: {
              height: 'unset',
              borderRadius: '0.5rem 0.5rem 0 0',
              width: '100vw',
              padding: '0',
              maxHeight: '100%',
              overflow: 'auto'
            }
          },
          CloseButton: {
            props: {
              paddingX: pxToRem(16),
              paddingY: pxToRem(16),
              top: 0,
              right: 0
            }
          }
        }}
      >
        <DrawerV2.CloseButton />
        <DrawerV2.Body padding={0}>{children}</DrawerV2.Body>
      </DrawerV2>
      {triggerClone}
    </>
  );
};

const DesktopWrapper = ({ children, trigger }: { children: React.ReactNode; trigger: React.ReactNode }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <PopoverV2
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      popperOptions={{
        offset: {
          offset: ({ reference, popper }) => {
            // bottom-right align reference and popper to cover the reference
            // [x, y]
            // -- x is measured from middle of reference (i.e. the trigger)
            // -- y is measured from the top of the reference but there's extra space
            return [(-1 * reference.width) / 2, reference.height * -1 - 8];
          }
        }
      }}
    >
      <PopoverV2.Trigger>{trigger}</PopoverV2.Trigger>
      <PopoverV2.Content borderRadius={3} overflow={'hidden'}>
        {children}
        <ButtonV2 position={'absolute'} shape={'rounded'} variant={'ghost'} top={0} right={0} padding={5} onClick={onClose} cursor={'pointer'}>
          <Close size="sm" />
        </ButtonV2>
      </PopoverV2.Content>
    </PopoverV2>
  );
};

const GuestGuideContent = ({ actions }: { actions: Record<'registry' | 'rsvp' | 'cars' | 'hotels' | 'calendar', boolean> }) => {
  const { t } = useTranslation('guestGuide');
  const telemetry = useGuestGuideTelemetry();

  useEffect(() => {
    telemetry.guideShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleClickAddToCalendar = () => telemetry.clickAddToCalendar();
  const handleClickEditRsvp = () => telemetry.clickEditRsvp();
  const handleClickCarRentals = () => telemetry.clickCarRentals();
  const handleClickBrowseHotels = () => telemetry.clickBrowseHotels();
  const handleClickBrowseRegistry = () => telemetry.clickBrowseRegistry();
  return (
    <JoyKitThemeProvider>
      <Box backgroundColor={'white'} color={'inherit'} padding={6}>
        <Box typographyVariant={'hed4'} textAlign={['center', 'left']}>
          {t('guideTitle')()}
        </Box>
        <GreetingWithEventName marginTop={1} />
        <Box marginTop={5} display={'grid'} rowGap={pxToRem(12)}>
          {/* These action components help decouple business logic from presentation */}
          {actions.calendar && (
            <AddToCalendarAction>
              <Button icon={<AddToCalendarIcon size={'md'} />} onClick={handleClickAddToCalendar}>
                {t('buttonTextAddToCalendar')()}
              </Button>
            </AddToCalendarAction>
          )}
          {actions.rsvp && (
            <EditRSVPAction>
              <Button as="a" icon={<Rsvp size={'md'} />} onClick={handleClickEditRsvp}>
                {t('buttonTextEditRSVP')()}
              </Button>
            </EditRSVPAction>
          )}
          {/* Car Rentals TBD: for now nothing is rendered */}
          {actions.cars && (
            <CarRentalsAction>
              <Button as="a" icon={<Car size={'md'} />} onClick={handleClickCarRentals}>
                {t('buttonTextCarRental')()}
              </Button>
            </CarRentalsAction>
          )}
          {actions.hotels && (
            <HotelDealsAction>
              <Button as="a" icon={<DiscountCode size={'md'} />} onClick={handleClickBrowseHotels}>
                <Box as="span" marginRight={3}>
                  {t('buttonTextHotelDeals')()}
                </Box>
                <Deal marginLeft={'auto'} />
              </Button>
            </HotelDealsAction>
          )}
          {actions.registry && (
            <BrowseCouplesRegistryAction>
              <Button as="a" icon={<Gift size={'md'} />} onClick={handleClickBrowseRegistry}>
                {t('buttonTextRegistry')()}
              </Button>
            </BrowseCouplesRegistryAction>
          )}
        </Box>
      </Box>
    </JoyKitThemeProvider>
  );
};

/**
 * Converts a human-readable date string (e.g. "August 23, 2025") to UTC ISO string,
 * normalizing it to midnight UTC. This ensures consistent date handling across timezones
 * by stripping time components and using only the date portion.
 *
 * The UTC conversion is necessary to prevent unexpected date shifts when the calendar
 * library uses .getUTCDate(), since local timezone midnight could result in a different
 * UTC date depending on the user's timezone.
 *
 * @param finalizedDate - Human-readable date string (e.g. "August 23, 2025")
 * @returns UTC ISO string representing midnight of the given date
 */
const prepareFinalizedDate = (finalizedDate: string) => {
  const date = new Date(finalizedDate);
  const utcTimestamp = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

  return new Date(utcTimestamp).toISOString();
};

const AddToCalendarAction = ({ children }: { children: React.ReactElement | React.ReactText }) => {
  const { eventHandle, eventInfo } = useEventInfo();
  const [querySchedule, { data, loading }] = useGetGuestSiteScheduleLazyQuery({
    batchMode: 'fast',
    ssr: false
  });
  useEffect(() => {
    if (eventHandle) {
      querySchedule({
        variables: {
          eventHandle,
          isAdmin: false
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventHandle]);
  const finalizedDate = eventInfo?.finalizedDate?.dateString ? eventInfo.finalizedDate.dateString : undefined;
  const events = data?.eventByName?.info.schedule.formattedSchedules.flatMap(day => day.items);
  const mainEvent = events?.find(ev => ev.isMain);
  const eventData: Record<'startTime' | 'endTime' | 'description' | 'title' | 'location', string | undefined> = {
    startTime: undefined,
    endTime: undefined,
    description: undefined,
    title: undefined,
    location: undefined
  };
  if (loading) {
    return null;
  } else if (mainEvent) {
    eventData.startTime = mainEvent.startTime?.timestamp ? new Date(mainEvent.startTime.timestamp).toISOString() : undefined;
    eventData.endTime = mainEvent.endTime?.timestamp ? new Date(mainEvent.endTime.timestamp).toISOString() : undefined;
    eventData.description = mainEvent.description || undefined;
    eventData.title = mainEvent.name || undefined;
    eventData.location = mainEvent.location || undefined;
  } else if (finalizedDate) {
    eventData.startTime = prepareFinalizedDate(finalizedDate);
    // eventData.endTime = DateFNS.format(DateFNS.addDays(new Date(finalizedDate), 1), 'yyyyMMdd');
    eventData.location = eventInfo?.locationInfo ? addressStr(eventInfo?.locationInfo) : undefined;
    eventData.title = eventInfo?.eventDisplayName || undefined;
  }
  if (!eventData.startTime) {
    return null;
  }
  return (
    <AddToCalendar
      start={eventData.startTime}
      end={eventData.endTime}
      allDay={!eventData.endTime}
      location={eventData.location}
      description={eventData.description}
      title={eventData.title}
    >
      {children}
    </AddToCalendar>
  );
};

const GreetingWithEventName = (props: BoxProps) => {
  const { eventInfo, loading } = useEventInfo();
  const { t } = useTranslation('guestGuide');
  // todo: loading
  return loading ? (
    <Skeleton />
  ) : (
    <Box typographyVariant={'body2'} color={'mono12'} textAlign={['center', 'left']} whiteSpace={'pre-line'} {...props}>
      {t('guideDescription')({ eventName: eventInfo?.eventDisplayName })}
    </Box>
  );
};

const EditRSVPAction = ({ children }: { children: React.ReactElement<{ href: string; target: string; as: 'a' }, typeof Button> }) => {
  const { eventHandle } = useEventInfo();
  // Cloning children because we don't want to just wrap the button, we want to hook into it by settings up the href
  // Cloning also alleviates issue you get with tabIndex and focus you would get from wrapping
  const clonedChild = React.cloneElement(children, {
    as: 'a',
    href: `/${eventHandle}/rsvp`
  });
  return clonedChild;
};

const CarRentalsAction = ({ children }: { children: React.ReactNode }) => {
  return null;
};

const HotelDealsAction = ({ children }: { children: React.ReactElement<{ href: string; target: string; as: 'a' }, typeof Button> }) => {
  const { eventHandle } = useEventInfo();
  const helper = useHotelRoutePaths();
  const route =
    eventHandle &&
    helper.hotelSearch.buildRelativeUrl(eventHandle, {
      utm_campaign: 'guest_guide',
      utm_medium: 'web',
      utm_source: 'guest_site'
    });
  // cloning children to separate action from presentation
  const clonedChild = React.cloneElement(children, {
    as: 'a',
    href: route
  });
  return route ? clonedChild : null;
};

/**
 * This component will navigate the user to the registry page on click of children
 * NOTE: children will be hidden when the action is rendered on the registry page
 */
const BrowseCouplesRegistryAction = ({ children }: { children: React.ReactElement<{ href: string; target: string; as: 'a' }, typeof Button> }) => {
  const { eventHandle } = useEventInfo();
  const registryPathname = `/${eventHandle}/registry`;
  const alreadyOnRegistryPage = withWindow(global => registryPathname === global.location.pathname, false);
  const clonedChild = React.cloneElement(children, {
    as: 'a',
    href: registryPathname
  });
  return alreadyOnRegistryPage ? null : clonedChild;
};

const StyledChevron = styled(ChevronRight)``;
const Button = React.forwardRef<HTMLButtonElement, { children: React.ReactNode; icon: React.ReactNode; as?: 'button' | 'a'; onClick?: () => {} }>(
  ({ children, icon, as = 'button', ...props }, ref) => {
    const theme = useTheme();
    return (
      <Box
        ref={ref}
        as={as}
        typographyVariant={'button2'}
        width={'100%'}
        padding={4}
        display={'flex'}
        alignItems={'center'}
        // there might be better way to attach the action to the button, but this is to let the parent action component take the tab focus
        // tabIndex={-1}
        gap={4}
        borderRadius={3}
        boxShadow={`inset 0 0 0 1px ${theme.colors.mono5}`}
        transition={`all ${STANDARD_TRANSITION}`}
        _hover={{
          boxShadow: `inset 0 0 0 2px ${theme.colors.black}`,
          [StyledChevron]: {
            color: theme.colors.black
          }
        }}
        // need to spread box props for trigger of Popover
        {...props}
      >
        {icon}
        <Box flex={1} display={'flex'} justifyContent={'flex-start'} alignItems={'center'}>
          {children}
        </Box>
        <StyledChevron transition={`color ${STANDARD_TRANSITION}`} size={'sm'} marginLeft={'auto'} color={'typeDisabledLight'} />
      </Box>
    );
  }
);

const Deal = (props: BoxProps) => {
  const { t } = useTranslation('guestGuide');
  return (
    <Box
      fontFamily={'Inter UI'}
      fontWeight={600}
      fontSize={pxToRem(10)}
      paddingX={pxToRem(6.5)}
      letterSpacing={0}
      height={pxToRem(24)}
      display={'inline-flex'}
      justifyContent={'center'}
      alignItems={'center'}
      color={'white'}
      backgroundColor={'newUIBrandPrimary'}
      borderRadius={3}
      {...props}
    >
      {t('tagDeals')()}
    </Box>
  );
};
